
import * as React from 'react';
import { useState } from 'react';

import { Alert, Heading } from '@/components/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { ForgotPassword } from '@/pages/Login/Content/ForgotPassword';
import { LoginForm } from '@/pages/Login/Content/LoginForm';
import { LoginOTP } from '@/pages/Login/Content/LoginOTP';
import { ResetPassword } from '@/pages/Login/Content/ResetPassword';

export default function LoginPage({ initialAuthState = 'login', code = '' }) {
    const emptyAlert = { active: false, message: '', tone: '', title: '' };

    const
        [alert, setAlert] = useState(emptyAlert),
        [authState, setAuthState] = useState(initialAuthState), // login, otp, forgotPassword, resetPassword
        [credentials, setCredentials] = useState({ email: '', password: '' });

    const deactivateAlert = () => {
        setAlert(emptyAlert);
    };

    const handleAlert = (message, tone = '', title) => {
        deactivateAlert();

        setTimeout(() => {
            setAlert({ active: true, message, tone, title });
        }, 500);
    };

    const handleBackToLogin = () => {
        window.history.replaceState(null, '', '/login');
        setAuthState('login');
    };

    const handleForgotPassword = (email) => {
        setCredentials({ email, password: '' });
        setAuthState('forgotPassword');
    };

    const handleLoginSuccess = (email, password) => {
        setCredentials({ email, password });
        setAuthState('otp');
    };

    return (
        <div className="tw-grid tw-min-h-screen-dvh tw-w-full tw-grid-cols-1 lg:tw-grid-cols-2">
            {/* Left-side area */}
            <div className="tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-screen tw-bg-surface tw-p-6 dark:tw-bg-gray-800 lg:tw-p-10">
                <div className="tw-w-full tw-max-w-xs sm:tw-max-w-sm 2xl:tw-max-w-md">
                    <div className="tw-mb-6 tw-flex tw-justify-center">
                        <img src="/images/wisemetering/color-logo-horizontal.png" alt=""/>
                    </div>
                    <div className="tw-h-auth-form-area tw-w-full">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={authState} // Ensures component re-renders on authState change
                                initial={{ x: '100%', opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: '-100%', opacity: 0 }}
                                transition={{ duration: 0.5 }}
                                className="tw-w-full tw-h-full"
                            >
                                {authState === 'login' && (
                                    <LoginForm onOtpRequest={handleLoginSuccess} onAlert={handleAlert} onForgotPassword={handleForgotPassword}/>
                                )}
                                {authState === 'otp' && (
                                    <LoginOTP email={credentials.email} onAlert={handleAlert} password={credentials.password} backToLogin={handleBackToLogin}/>
                                )}
                                {authState === 'forgotPassword' && (
                                    <ForgotPassword loginEmail={credentials.email} onAlert={handleAlert} backToLogin={handleBackToLogin}/>
                                )}
                                {authState === 'resetPassword' && (
                                    <ResetPassword onAlert={handleAlert} code={code} backToLogin={handleBackToLogin}/>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    <div>
                        <div className="tw-w-full tw-max-w-sm 2xl:tw-max-w-md tw-absolute tw-top-0 tw-pr-14 sm:tw-pr-0 tw-pt-4">
                            <AnimatePresence>
                                {alert.active ? (
                                    <motion.div
                                        key={alert.title + alert.message}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        initial={{ opacity: 0 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <Alert
                                            description={alert.message}
                                            dismissible
                                            duration={5000}
                                            onDismiss={deactivateAlert}
                                            title={alert.title}
                                            tone={alert.tone}
                                        />
                                    </motion.div>
                                ) : null}
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right-side image with overlay heading */}
            <div className="tw-hidden tw-relative lg:tw-block">
                <img
                    src="/images/placeholder.webp"
                    alt=""
                    aria-hidden="true"
                    className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-object-center"
                />
                <div className="tw-relative tw-p-20 tw-text-background">
                    <Heading as="h2" variant="display">
                        <span className="tw-block">Towards</span>
                        <span className="tw-block">a Wise Future</span>
                    </Heading>
                </div>
            </div>
        </div>

    );
}
